import { useMemo } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { RequestModel, RequestStatus } from "model";
import { fetchRequestIndex } from "utils/api";

import { LocalStorageFilterValueTypes } from "~components/Organisms/IndexFilters/types";

import { matchesDate, matchesSchool, matchesStatus } from "./utils";

/** Types */
type ActiveRequests = "active-requests";
type PastRequests = "past-requests";
export type RequestType = ActiveRequests | PastRequests;

/** Constants */
export const SegmentName = {
  Unfilled: "unfilled",
  Waitlist: "waitlist",
  Filled: "filled",
  Completed: "completed",
  Canceled: "canceled",
  Review: "review",
} as const;

export type SegmentNameUnion = (typeof SegmentName)[keyof typeof SegmentName];

export const activeSegments: Array<SegmentNameUnion> = [
  SegmentName.Waitlist,
  SegmentName.Unfilled,
  SegmentName.Filled,
];
export const pastSegments: Array<SegmentNameUnion> = [
  SegmentName.Review,
  SegmentName.Completed,
  SegmentName.Unfilled,
  SegmentName.Canceled,
];

export const doesReqMatchSegment = (segment: SegmentNameUnion, req: RequestModel) => {
  switch (segment) {
    case SegmentName.Unfilled:
      return req.status === RequestStatus.Submitted && req.poolCount === 0;
    case SegmentName.Waitlist:
      return (
        (req.status === RequestStatus.Submitted ||
          req.status === RequestStatus.ConfirmationPending) &&
        req.poolCount > 0
      );
    case SegmentName.Filled:
      return req.status === RequestStatus.Filled;
    case SegmentName.Completed:
      return req.status === RequestStatus.Completed;
    case SegmentName.Canceled:
      return req.status === RequestStatus.Canceled;
    case SegmentName.Review:
      return req.status === RequestStatus.Completed && !req.hasFeedback;
    default:
      // eslint-disable-next-line no-console
      console.error("Found a request that doesn't match any segments.");
      return;
  }
};

type UseRequestsParams = {
  showHistory?: boolean;
  filterSegment: SegmentNameUnion;

  // TODO: This will become the default once Admin Filters
  // gets released.
  getUnfilteredData?: boolean;
};
export type UseRequestsReturn = ReturnType<typeof useRequests>["data"];
export function useRequests(params: UseRequestsParams) {
  const { showHistory = false, filterSegment, getUnfilteredData = false } = params;

  return useQuery({
    queryKey: ["requests", !showHistory ? "active" : "past"],
    queryFn: async () => {
      return await fetchRequestIndex({
        showHistory,
      });
    },
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    select: ({ data }) => {
      const counts = {
        active: {
          [SegmentName.Unfilled]: 0,
          [SegmentName.Waitlist]: 0,
          [SegmentName.Filled]: 0,
        },
        past: {
          [SegmentName.Completed]: 0,
          [SegmentName.Canceled]: 0,
          [SegmentName.Unfilled]: 0,
          [SegmentName.Review]: 0,
        },
      };

      const requestList: RequestModel[] = data.map((requestData: unknown) => {
        const req = new RequestModel(requestData);
        /*
        ACTIVE SEGMENTS (and unfilled past segment)
        */

        // Compute count here so it gets all the goodies of useQuery
        if (doesReqMatchSegment(SegmentName.Unfilled, req)) {
          if (showHistory) {
            counts.past[SegmentName.Unfilled] += 1;
          } else {
            counts.active[SegmentName.Unfilled] += 1;
          }
        }
        if (doesReqMatchSegment(SegmentName.Waitlist, req)) {
          counts.active[SegmentName.Waitlist] += 1;
        }
        // If request has the status "filled"
        if (doesReqMatchSegment(SegmentName.Filled, req)) {
          counts.active[SegmentName.Filled] += 1;
        }
        /*
        PAST SEGMENTS
        */

        // If request has the status "Completed"
        if (doesReqMatchSegment(SegmentName.Completed, req)) {
          counts.past[SegmentName.Completed] += 1;
        }
        // If request has the status "Canceled"
        if (doesReqMatchSegment(SegmentName.Canceled, req)) {
          counts.past[SegmentName.Canceled] += 1;
        }
        // If request has the status "Completed" and has
        // no review item
        if (doesReqMatchSegment(SegmentName.Review, req)) {
          counts.past[SegmentName.Review] += 1;
        }

        return req;
      });

      return {
        requests: getUnfilteredData
          ? requestList
          : requestList.filter((req: RequestModel) => doesReqMatchSegment(filterSegment, req)),
        counts,
      };
    },
  });
}

export function useRequestIndexFilters(
  filters: LocalStorageFilterValueTypes,
  requests?: RequestModel[],
): RequestModel[] {
  return useMemo(() => {
    if (!requests) return [];
    return requests?.filter(
      (item) =>
        matchesStatus(item, filters.statuses) &&
        matchesSchool(item, filters.schools) &&
        matchesDate({
          item,
          dateOption: filters?.dateOption,
          customDate: filters?.customDates,
        }),
    );
  }, [requests, filters]);
}
